import { useEffect, useRef } from 'react';
import { rootStore, useClient } from 'easemob-chat-uikit';
import { chatStorage } from '@/services/chatStorage';

/**
 * 聊天消息本地同步Hook
 * 负责：
 * 1. 登录后加载本地消息
 * 2. 监听新消息并保存到本地
 * 3. 同步会话列表到本地
 */
export const useChatSync = (userId?: string) => {
  const client = useClient();
  const hasLoadedLocal = useRef(false);
  const syncIntervalRef = useRef<number>();

  useEffect(() => {
    if (!userId || !client) return;

    // 设置当前用户
    chatStorage.setCurrentUser(userId);

    // 只在首次加载时从本地恢复数据
    if (!hasLoadedLocal.current) {
      loadLocalData();
      hasLoadedLocal.current = true;
    }

    // 监听新消息
    const messageListener = {
      onTextMessage: (message: any) => handleNewMessage(message),
      onImageMessage: (message: any) => handleNewMessage(message),
      onAudioMessage: (message: any) => handleNewMessage(message),
      onVideoMessage: (message: any) => handleNewMessage(message),
      onFileMessage: (message: any) => handleNewMessage(message),
      onCustomMessage: (message: any) => handleNewMessage(message),
    };

    client.addEventHandler('MESSAGE_SYNC', messageListener);

    // 定期同步会话列表和消息到本地（每30秒）
    syncIntervalRef.current = setInterval(() => {
      syncToLocal();
    }, 30000);

    return () => {
      client.removeEventHandler('MESSAGE_SYNC');
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      // 在卸载时做最后一次同步
      syncToLocal();
    };
  }, [userId, client]);

  /**
   * 从本地加载数据
   */
  const loadLocalData = async () => {
    try {
      console.log('开始从IndexedDB加载消息...');
      
      // 加载会话列表
      const localConversations = await chatStorage.getConversations();
      if (localConversations.length > 0) {
        console.log(`从IndexedDB加载了 ${localConversations.length} 个会话`);
        
        // 将会话添加到store（但不覆盖远程数据）
        localConversations.forEach(cvs => {
          const exists = rootStore.conversationStore.conversationList.find(
            (c: any) => c.conversationId === cvs.conversationId
          );
          
          if (!exists) {
            rootStore.conversationStore.addConversation({
              conversationId: cvs.conversationId,
              chatType: cvs.chatType,
              name: cvs.name,
              lastMessage: cvs.lastMessage,
              unreadCount: cvs.unreadCount,
            } as any);
          }
        });
      }

      // 获取统计信息
      const stats = await chatStorage.getStats();
      console.log(`IndexedDB统计: ${stats.messageCount} 条消息, ${stats.conversationCount} 个会话`);
    } catch (error) {
      console.error('从IndexedDB加载数据失败:', error);
    }
  };

  /**
   * 处理新消息
   */
  const handleNewMessage = (message: any) => {
    try {
      // 保存新消息到IndexedDB
      const storedMessage = {
        id: message.id,
        conversationId: message.to === userId ? message.from : message.to,
        chatType: message.chatType || 'singleChat',
        from: message.from,
        to: message.to,
        type: message.type,
        msg: message.msg || '',
        time: message.time,
        ...message,
      };

      chatStorage.saveMessages([storedMessage], storedMessage.conversationId);
      console.log('新消息已保存到IndexedDB:', storedMessage.id);
    } catch (error) {
      console.error('保存新消息到IndexedDB失败:', error);
    }
  };

  /**
   * 同步当前数据到本地
   */
  const syncToLocal = async () => {
    try {
      // 同步会话列表
      const conversations = rootStore.conversationStore.conversationList || [];
      if (conversations.length > 0) {
        await chatStorage.saveConversations(
          conversations.map((cvs: any) => ({
            conversationId: cvs.conversationId,
            chatType: cvs.chatType,
            name: cvs.name,
            lastMessage: cvs.lastMessage,
            unreadCount: cvs.unreadCount,
          }))
        );
      }

      // 同步消息（从messageStore获取）
      const messageStore = rootStore.messageStore.message;
      if (messageStore) {
        const allMessages: any[] = [];
        
        // 遍历所有会话的消息
        ['singleChat', 'groupChat'].forEach(chatType => {
          const messages = (messageStore as any)[chatType] || {};
          Object.entries(messages).forEach(([cvId, msgs]: [string, any]) => {
            if (Array.isArray(msgs)) {
              msgs.forEach(msg => {
                allMessages.push({
                  id: msg.id,
                  conversationId: cvId,
                  chatType,
                  from: msg.from,
                  to: msg.to,
                  type: msg.type,
                  msg: msg.msg || '',
                  time: msg.time,
                  ...msg,
                });
              });
            }
          });
        });

        if (allMessages.length > 0) {
          await chatStorage.saveMessages(allMessages);
          console.log(`已同步 ${allMessages.length} 条消息到IndexedDB`);
        }
      }
    } catch (error) {
      console.error('同步数据到IndexedDB失败:', error);
    }
  };

  return {
    loadLocalData,
    syncToLocal,
  };
};
